import { Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import moment from 'moment';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { getAllUsersFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard, EmailBody } from '../../../../../types/files';
import ArrayUtils from '../../../../../utils/ArrayUtils';
import EmailsModal from '../../../../modals/tables/dashboards/rows/EmailsModal';

export const useLocalStyles = makeStyles((theme) => ({
    count: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    noCount: {
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    received: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 400,
        textDecoration: 'none',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    label: {
        marginLeft: 8,
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: '#7bd4d4',
        "&:hover": {
            textDecoration: 'underline',
        }
    },
    icon: {
        width: 20,
        height: 20,
    },
    url: {
        display: 'block',
        fontFamily: 'Lato',
        fontSize: '0.8rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
}));

const EmailsCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const localClasses = useLocalStyles();
    const { userGroup, user } = useContext(AuthContext);
    const [emailsModalOpen, setEmailsModalOpen] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<{ firstName: string, lastName: string }>({ firstName: '', lastName: '' });

    const emailActivities = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];

        return ArrayUtils.sortByDescending(activityItems ?? [], 'createdAt');
    }, [dashboard]);

    const viewableEmails = useMemo(() =>
        emailActivities.filter(emailBody => {
            const senderName = emailBody?.from?.replace(/<.*?>/g, '')?.trim()?.toLowerCase();
            const senderEmail = emailBody?.from?.match(/<(.+?)>/)?.[1]?.trim()?.toLowerCase();

            // return true;
            return ((senderEmail === email) || (senderName?.includes(name.firstName) && senderName?.includes(name.lastName)));
        }) as EmailBody[]
        , [email, name, emailActivities]);

    useEffect(() => {
        getAllUsersFunc(userGroup).then((users) => {
            const lookUpUser = users.find(({ username }) => username === user.username);

            setEmail(user?.attributes?.email?.toLowerCase() || lookUpUser?.email?.toLowerCase());
            setName({ firstName: lookUpUser?.firstName?.toLowerCase() || '', lastName: lookUpUser?.lastName?.toLowerCase() || '' });
        });
    }, [userGroup, user]);

    return (<>
        <Stack width="100%">
            {(emailActivities.length > 0) ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start"
                    onClick={() => !!viewableEmails.length ? setEmailsModalOpen(true) : undefined}
                    sx={{ cursor: !!viewableEmails.length ? 'pointer' : undefined }}>
                    <Stack direction="column" alignItems="flex-start" justifyContent="center">
                        <Typography className={classNames(localClasses.count, !emailActivities.length && localClasses.noCount)}
                            sx={!!viewableEmails.length ? { "&:hover": { textDecoration: 'underline' } } : {}}>
                            {`${emailActivities.length}`}
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '0.9rem !important', fontWeight: '400' }} display="inline">
                                {!emailActivities.length ? '' : ` email${emailActivities.length > 1 ? 's' : ''} received`}
                            </Typography>
                        </Typography>
                        <Typography className={localClasses.received}>
                            {`Last: ${!!emailActivities.length ? moment(emailActivities[0].createdAt).fromNow() : moment(dashboard.createdAt).fromNow()}`}
                        </Typography>
                    </Stack>
                </Stack>
            ) : (<Typography className={localClasses.url} fontSize="0.9rem !important" color={`${theme.colors.neutral['500']} !important`}>
                {'No emails'}
            </Typography>
            )}
        </Stack>
        {emailsModalOpen && (
            <EmailsModal
                open={emailsModalOpen}
                viewableEmails={viewableEmails}
                dashboard={dashboard}
                onClose={() => setEmailsModalOpen(false)} />

        )}
    </>);
}

export default memo(EmailsCell);
